import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/sonner';
import { useGeolocation } from '@/hooks/use-geolocation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface Meeting {
  id: string;
  title: string;
  datetime: string;
  location: {
    latitude: number;
    longitude: number;
    radius: number;
  };
}

const AttendanceCheckInPage: React.FC = () => {
  const { meetingCode } = useParams<{ meetingCode: string }>();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [locationVerified, setLocationVerified] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    stateCode: '',
  });
  
  const { 
    position,
    error: locationError,
    loading: locationLoading 
  } = useGeolocation();

  useEffect(() => {
    // Fetch meeting details
    const fetchMeeting = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/meetings/${meetingCode}`);
        if (!response.ok) {
          throw new Error('Meeting not found');
        }
        const data = await response.json();
        setMeeting(data);
      } catch (error) {
        toast.error('Failed to load meeting details');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchMeeting();
  }, [meetingCode, navigate]);

  const verifyLocation = () => {
    if (!meeting || !position?.latitude || !position?.longitude) return;

    const distance = calculateDistance(
      position.latitude,
      position.longitude,
      meeting.location.latitude,
      meeting.location.longitude
    );

    if (distance <= meeting.location.radius) {
      setLocationVerified(true);
      toast.success('Location verified successfully');
    } else {
      toast.error(`You are too far from the meeting location. Required distance: ${meeting.location.radius}m`);
    }
  };

  const handleCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!locationVerified || !meeting) return;

    setCheckingIn(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/attendance/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meetingCode,
          name: formData.fullName,
          stateCode: formData.stateCode,
          coords: {
            lat: position?.latitude,
            lng: position?.longitude
          }
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to check in');
      }

      toast.success('Check-in successful!');
      navigate('/');
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to check in. Please try again.');
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-2xl font-bold mb-4">Meeting not found</h1>
        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-2xl mx-auto"
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{meeting.title}</CardTitle>
            <CardDescription>
              Meeting Date: {new Date(meeting.datetime).toLocaleString()}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Location Verification</h2>
              
              {locationError ? (
                <div className="flex items-center text-red-500">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <span>Error accessing location: {locationError}</span>
                </div>
              ) : locationLoading ? (
                <div className="flex items-center">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  <span>Getting your location...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 mr-2" />
                    <span>Your location: {position?.latitude?.toFixed(6)}, {position?.longitude?.toFixed(6)}</span>
                  </div>
                  
                  {!locationVerified ? (
                    <Button 
                      onClick={verifyLocation}
                      className="w-full"
                    >
                      Verify Location
                    </Button>
                  ) : (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      <span>Location verified</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {locationVerified && (
              <form onSubmit={handleCheckIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stateCode">State Code</Label>
                  <Input
                    id="stateCode"
                    value={formData.stateCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, stateCode: e.target.value }))}
                    placeholder="Enter your state code"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={checkingIn}
                >
                  {checkingIn ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Checking in...
                    </>
                  ) : (
                    'Check In'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

// Helper function to calculate distance between two points using Haversine formula
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

export default AttendanceCheckInPage;
